import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import axiosInstance from '../services/api';

const PrivateRoute = ({ allowedRoles }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authStatus, setAuthStatus] = useState('checking');
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null); // Add userData state

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const validateAuth = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) throw new Error('missing_token');

        const response = await axiosInstance.get('/profile/', {
          signal: controller.signal
        });

        if (isMounted) {
          setUserData(response.data);
          const userRole = response.data.system_role?.toLowerCase();
          
          if (!allowedRoles.includes(userRole)) {
            setAuthStatus('unauthorized');
          } else {
            setAuthStatus('authorized');
          }
        }

      } catch (error) {
        if (error.name === 'CanceledError') return;
        
        console.error('Auth error:', error);
        localStorage.clear();
        navigate('/login', {
          state: { error: 'Session expired' },
          replace: true
        });
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    validateAuth();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [navigate, allowedRoles, location.pathname]);

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh'
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Securing Connection...
        </Typography>
      </Box>
    );
  }

  switch(authStatus) {
    case 'authorized':
      return <Outlet context={{ userData }} />;

    case 'unauthorized':
      navigate('/unauthorized', { replace: true });
      return null;

    default:
      navigate('/login', {
        state: {
          from: location.pathname,
          error: 'Session expired. Please login again.'
        },
        replace: true
      });
      return null;
  }
};

export default PrivateRoute;